import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Package, Users, ShoppingCart, TrendingUp, RefreshCw, LogOut } from 'lucide-react';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adminUser, setAdminUser] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');

  useEffect(() => {
    // Check if admin is logged in
    const token = localStorage.getItem('admin_token');
    const user = localStorage.getItem('admin_user');
    
    if (!token || !user) {
      navigate('/admin/login');
      return;
    }
    
    setAdminUser(JSON.parse(user));
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      // Fetch stats
      const statsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/stats`, { headers });
      if (!statsRes.ok) throw new Error('Unauthorized');
      const statsData = await statsRes.json();
      setStats(statsData);
      
      // Fetch orders
      const ordersRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/orders`, { headers });
      const ordersData = await ordersRes.json();
      setOrders(ordersData);
      
      // Fetch products
      const productsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/products`, { headers });
      const productsData = await productsRes.json();
      setProducts(productsData);
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      // If unauthorized, redirect to login
      if (error.message === 'Unauthorized') {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        navigate('/admin/login');
      }
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    navigate('/admin/login');
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const token = localStorage.getItem('admin_token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/orders/${orderId}/status?status=${newStatus}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      // Refresh orders
      fetchData();
      alert('Sipariş durumu güncellendi ve email gönderildi!');
    } catch (error) {
      console.error('Error updating order status:', error);
      alert('Hata oluştu!');
    }
  };

  const updateProductStock = async (productId, inStock) => {
    try {
      const token = localStorage.getItem('admin_token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/products/${productId}/stock?in_stock=${inStock}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      // Refresh products
      fetchData();
      alert('Stok durumu güncellendi!');
    } catch (error) {
      console.error('Error updating product stock:', error);
      alert('Hata oluştu!');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-blue-100 text-blue-800',
      shipped: 'bg-purple-100 text-purple-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusText = (status) => {
    const texts = {
      pending: 'Beklemede',
      confirmed: 'Onaylandı',
      shipped: 'Kargoya Verildi',
      delivered: 'Teslim Edildi',
      cancelled: 'İptal Edildi'
    };
    return texts[status] || status;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-junto-cream flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-junto-red animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-junto-cream py-16">
      <Helmet>
        <title>Admin Panel - JUNTO® Kahve</title>
      </Helmet>

      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Admin Panel</h1>
            <p className="text-gray-600">JUNTO® E-Ticaret Yönetim Paneli</p>
            {adminUser && (
              <p className="text-sm text-gray-500 mt-2">Hoş geldiniz, {adminUser.name}</p>
            )}
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center space-x-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
          >
            <LogOut className="w-5 h-5" />
            <span>Çıkış Yap</span>
          </button>
        </div>

        {/* Tabs */}
        <div className="mb-8 flex space-x-4 border-b border-gray-300">
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`px-6 py-3 font-semibold transition-colors ${
              activeTab === 'dashboard'
                ? 'text-junto-red border-b-2 border-junto-red'
                : 'text-gray-600 hover:text-junto-red'
            }`}
          >
            Dashboard
          </button>
          <button
            onClick={() => setActiveTab('orders')}
            className={`px-6 py-3 font-semibold transition-colors ${
              activeTab === 'orders'
                ? 'text-junto-red border-b-2 border-junto-red'
                : 'text-gray-600 hover:text-junto-red'
            }`}
          >
            Siparişler ({stats?.pending_orders})
          </button>
          <button
            onClick={() => setActiveTab('products')}
            className={`px-6 py-3 font-semibold transition-colors ${
              activeTab === 'products'
                ? 'text-junto-red border-b-2 border-junto-red'
                : 'text-gray-600 hover:text-junto-red'
            }`}
          >
            Ürünler
          </button>
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <ShoppingCart className="w-10 h-10 text-junto-red" />
                <span className="text-3xl font-bold text-gray-900">{stats.total_orders}</span>
              </div>
              <h3 className="text-gray-600 font-semibold">Toplam Sipariş</h3>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <Package className="w-10 h-10 text-yellow-500" />
                <span className="text-3xl font-bold text-gray-900">{stats.pending_orders}</span>
              </div>
              <h3 className="text-gray-600 font-semibold">Bekleyen Sipariş</h3>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <Package className="w-10 h-10 text-blue-500" />
                <span className="text-3xl font-bold text-gray-900">{stats.total_products}</span>
              </div>
              <h3 className="text-gray-600 font-semibold">Toplam Ürün</h3>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <TrendingUp className="w-10 h-10 text-green-500" />
                <span className="text-3xl font-bold text-gray-900">₺{stats.total_revenue.toFixed(2)}</span>
              </div>
              <h3 className="text-gray-600 font-semibold">Toplam Gelir</h3>
            </div>
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Sipariş No</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Müşteri</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Telefon</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Toplam</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Durum</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">İşlem</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {orders.map((order) => (
                    <tr key={order.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">#{order.id.substring(0, 8)}</td>
                      <td className="px-6 py-4 text-sm">
                        <div className="font-medium text-gray-900">{order.customer_name}</div>
                        <div className="text-gray-500">{order.customer_email}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{order.customer_phone}</td>
                      <td className="px-6 py-4 text-sm font-semibold text-gray-900">₺{order.total.toFixed(2)}</td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(order.status)}`}>
                          {getStatusText(order.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <select
                          value={order.status}
                          onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                          className="border border-gray-300 rounded-lg px-3 py-1 text-sm"
                        >
                          <option value="pending">Beklemede</option>
                          <option value="confirmed">Onaylandı</option>
                          <option value="shipped">Kargoya Verildi</option>
                          <option value="delivered">Teslim Edildi</option>
                          <option value="cancelled">İptal Edildi</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Products Tab */}
        {activeTab === 'products' && (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Ürün</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Kategori</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Fiyat</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Stok Durumu</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">İşlem</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {products.map((product) => (
                    <tr key={product.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <img
                            src={product.image}
                            alt={product.name}
                            className="w-12 h-12 object-cover rounded-lg mr-4"
                          />
                          <div className="font-medium text-gray-900">{product.name}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{product.category}</td>
                      <td className="px-6 py-4 text-sm font-semibold text-gray-900">₺{product.base_price}</td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          product.in_stock ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {product.in_stock ? 'Stokta' : 'Tükendi'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => updateProductStock(product.id, !product.in_stock)}
                          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                            product.in_stock
                              ? 'bg-red-100 text-red-700 hover:bg-red-200'
                              : 'bg-green-100 text-green-700 hover:bg-green-200'
                          }`}
                        >
                          {product.in_stock ? 'Stoktan Çıkar' : 'Stoğa Ekle'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
